import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchVocabsForReviewByTopicId, saveDictationProgress } from '../api/api';
import { VocabularyWord } from '../types';
import { ReactComponent as SpeakerIcon } from '../assets/speaker-icon.svg';
import ProgressStepper from '../components/ProgressStepper';
import EmptyState from '../components/EmptyState';

// --- CẤU TRÚC DỮ LIỆU & HÀM TIỆN ÍCH ---
interface PracticeSentence {
  sentenceText: string;
  parentWord: string;
  vocabularyId: string; // Thêm ID của từ vựng
  exampleNumber: 1 | 2;
  status: 'untouched' | 'correct' | 'incorrect';
}

const compareSentences = (userInput: string, correctSentence: string) => {
  const normalize = (str: string) => str.trim().toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?]/g, "");
  const userWords = normalize(userInput).split(/\s+/).filter(Boolean);
  const correctWords = normalize(correctSentence).split(/\s+/).filter(Boolean);

  if (userWords.length !== correctWords.length) {
    const result = correctWords.map((correctWord, index) => {
      const userWord = userWords[index];
      if (userWord && userWord === correctWord) {
        return { word: userWord, status: 'correct' as const };
      } else if (userWord) {
        return { word: userWord, status: 'incorrect' as const };
      }
      return { word: correctWord, status: 'missing' as const };
    });
    return { isCorrect: false, result };
  }

  let allCorrect = true;
  const result = correctWords.map((correctWord, index) => {
    if (userWords[index] === correctWord) {
      return { word: userWords[index], status: 'correct' as const };
    } else {
      allCorrect = false;
      return { word: userWords[index], status: 'incorrect' as const };
    }
  });

  return { isCorrect: allCorrect, result };
};


// --- COMPONENT CHÍNH ---
const ListeningPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const navigate2 = useNavigate();

  const [sentences, setSentences] = useState<PracticeSentence[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [comparisonResult, setComparisonResult] = useState<{ word: string, status: string }[] | null>(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (topicId) {
      const getPracticeData = async () => {
        setLoading(true);
        try {
          const words = await fetchVocabsForReviewByTopicId(topicId, '2');
          const practiceList: PracticeSentence[] = [];
          (words || []).forEach(word => {
            if (word.vocabulary.example1) {
              practiceList.push({
                sentenceText: word.vocabulary.example1,
                parentWord: word.vocabulary.word,
                vocabularyId: word.vocabulary.id,
                exampleNumber: 1,
                // Dùng trạng thái từ backend nếu có, nếu không thì 'untouched'
                status: word.example1Status === 'correct' ? 'correct' : 'untouched'
              });
            }
            if (word.vocabulary.example2) {
              practiceList.push({
                sentenceText: word.vocabulary.example2,
                parentWord: word.vocabulary.word,
                vocabularyId: word.vocabulary.id,
                exampleNumber: 2,
                status: word.example2Status === 'correct' ? 'correct' : 'untouched'
              });
            }
          });
          setSentences(practiceList);
        } catch (error) {
          console.error("Failed to load topic:", error);
          setSentences([]);
        } finally {
          setLoading(false);
        }
      };
      getPracticeData();
    }
  }, [topicId]);

  useEffect(() => {
    const loadVoices = () => {
      setVoices(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.onvoiceschanged = loadVoices;
    loadVoices();
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const currentSentence = sentences[currentIndex];

  const playAudio = () => {
    if (currentSentence) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(currentSentence.sentenceText);

      const femaleVoice = voices.find(voice =>
        voice.lang === 'en-US' &&
        (voice.name.includes('Female') || voice.name.includes('Zira') || voice.name.includes('Samantha'))
      );

      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }

      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleCheckAnswer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInput.trim() || !currentSentence) return;

    const { isCorrect: isAnswerCorrect, result } = compareSentences(userInput, currentSentence.sentenceText);
    setComparisonResult(result);
    setIsCorrect(isAnswerCorrect);

    // Nếu trả lời đúng, gọi API để lưu tiến độ
    if (isAnswerCorrect) {
      try {
        saveDictationProgress(currentSentence.vocabularyId, currentSentence.exampleNumber, 'correct');
      } catch (error) {
        console.error("Failed to save progress:", error);
        // Có thể thông báo lỗi cho người dùng nếu cần
      }
    }

    // Khai báo tường minh kiểu dữ liệu cho status mới
    const newStatus: 'correct' | 'incorrect' = isAnswerCorrect ? 'correct' : 'incorrect';
    const updatedSentences = sentences.map((s, i) =>
      i === currentIndex ? { ...s, status: newStatus } : s
    );
    setSentences(updatedSentences);
  };

  const navigateToSentence = (index: number) => {
    setCurrentIndex(index);
    setUserInput('');
    setComparisonResult(null);
    setIsCorrect(false);
  };

  const handleNext = () => {
    if (currentIndex < sentences.length - 1) {
      navigateToSentence(currentIndex + 1);
    } else {
      const mistakes = sentences.filter(s => s.status === 'incorrect');
      if (mistakes.length > 0) {
        if (window.confirm(`Bạn còn ${mistakes.length} câu sai. Bạn có muốn ôn tập lại không?`)) {
          setSentences(mistakes);
          navigateToSentence(0);
        } else {
          navigate(-1);
        }
      } else {
        alert("Chúc mừng! Bạn đã hoàn thành tất cả các câu!");
        navigate(-1);
      }
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      navigateToSentence(currentIndex - 1);
    }
  };

  if (loading) return <div>Loading exercise...</div>;
  if (sentences.length === 0) {
    return (
      <EmptyState
        title="No Sentences to Practice"
        message="This topic doesn't have any example sentences for the dictation exercise."
        action={
          <button onClick={() => navigate2(-1)} className="btn btn-primary">
            ‹ Go Back
          </button>
        }
      />
    );
  }

  return (
    <div className="quiz-container glass-effect">
      <header className="page-header">
        <button onClick={() => navigate(-1)} className="btn-back">‹ Back</button>
        <div className="topic-title">
          <h3>Listening - Dictation</h3>
          <p>Word: <strong>{currentSentence.parentWord}</strong></p>
        </div>
      </header>

      <ProgressStepper
        items={sentences}
        currentIndex={currentIndex}
        onStepClick={navigateToSentence}
      />

      <div className="listening-controls">
        <p>Listen to the sentence and type what you hear:</p>
        <button onClick={playAudio} className="speak-button">
          <SpeakerIcon />
        </button>
      </div>

      <form onSubmit={handleCheckAnswer}>
        <textarea
          className="dictation-input"
          rows={4}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder="Type the sentence here..."
          disabled={isCorrect}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isCorrect}
          style={{ width: '100%', marginTop: '10px' }}
        >
          Check
        </button>
      </form>

      {comparisonResult && (
        <div className={`feedback-box ${isCorrect ? 'correct' : 'incorrect'}`}>
          <p style={{ fontWeight: 'bold', margin: 0 }}>
            {isCorrect ? 'Excellent!' : 'Not quite, the correct words are highlighted:'}
          </p>
          <div className="feedback-comparison">
            {comparisonResult.map((item, index) => (
              <span key={index} className={`word-${item.status}`}>
                {item.word}
              </span>
            ))}
          </div>
        </div>
      )}

      <footer className="page-footer">
        <button onClick={handlePrev} className="btn btn-nav" disabled={currentIndex === 0}>‹ Previous</button>
        <span className="progress-text">{currentIndex + 1} / {sentences.length}</span>
        <button onClick={handleNext} className="btn btn-nav">Next ›</button>
      </footer>
    </div>
  );
};

export default ListeningPage;